import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { SubSink } from "subsink";
import { CHART_DOWNLOAD_REQUEST_FAILURE, CHART_DOWNLOAD_REQUEST_SUCCESS } from "../../core/messaging/messaging.constant";
import { MessagingService } from "../../core/messaging/messaging.service";
import { SeverityType } from "../../core/messaging/severity-type.enum";
import { FormService } from "../../dynamic-forms/form.service";
import { Textbox } from "../../dynamic-forms/inputs/textbox/textbox.model";
import { ChartLakeStatisticsService } from "../../platform/api/chart-lake-statistics/chart-lake-statistics.service";
import { TenantStatisticsResponse } from "../../platform/api/chart-lake-statistics/tenant-statistics-response.model";
import { ChartService } from "../../platform/api/chart-lake/chart.service";
import { MemberV2Service } from "../../platform/api/member-v2/member-v2.service";
import { CHART_QUERY_GRID } from "../../platform/modules/member/chase-detail/chase-detail-chart/attributes";
import { ChartRouteFilters } from "../../platform/modules/member/member-detail/member-detail-body/member-detail-body-grids-charts/chart-route-filter.model";
import { ArrayHelper } from "../../utilities/contracts/array-helper";
import { DateHelper } from "../../utilities/contracts/date-helper";
import { ObjectHelper } from "../../utilities/contracts/object-helper";
import { StringHelper } from "../../utilities/contracts/string-helper";
import { CustomPaginationComponent } from "../custom-pagination/custom-pagination.component";
import { IFilter } from "../grid/basic-filter-applied/basic-filter-applied.component";
import { BasicGridComponent } from "../grid/basic-grid/basic-grid.component";
import { BulkAction } from "../grid/bulk-actions/bulk-action.model";
import { GridView } from "../grid/grid-menu/grid-views/grid-view.model";
import { GridViewsState } from "../grid/grid-menu/grid-views/grid-views-state.model";
import { GridViewsService } from "../grid/grid-menu/grid-views/grid-views.service";
import { GridStateService } from "../grid/grid-state.service";
import { GridColumnDefinition } from "../grid/models/grid-column-definition.model";
import { GridConfiguration } from "../grid/models/grid-configuration.model";

@Component({
  selector: "app-chart-grid",
  templateUrl: "./chart-grid.component.html",
  styleUrls: ["./chart-grid.component.scss"],
})
export class ChartGridComponent implements OnInit, OnDestroy {

  @ViewChild(CustomPaginationComponent, { static: true })
  paginationComponent: CustomPaginationComponent;
  @ViewChild(BasicGridComponent, { static: true })
  basicGridComponent: BasicGridComponent;
  @Input() loadFilter = false;
  @Input() disableFilter = false;
  @Input() additionalColumns: GridColumnDefinition[] = [];
  stateName = "chart-lake-chart-query";
  chartData: any[];
  viewAttributeId = CHART_QUERY_GRID.attributeId;
  @Input() filters: any = {};
  request: IFilter[] | undefined = [];
  rows: number;
  form: FormGroup;
  chartId: Textbox;
  memberId: Textbox;
  memberFirstName: Textbox;
  memberLastName: Textbox;
  providerName: Textbox;
  groupName: Textbox;
  fileName: Textbox;
  dateOfServiceFrom: Textbox;
  dateOfServiceTo: Textbox;
  memberDateOfBirth: Textbox;
  configuration: GridConfiguration;
  actions: BulkAction[] = [];
  totalChartCount: number;
  @Input() showCount = false;
  @Input() routeParameters: ChartRouteFilters = null;
  @Input() hideFilterItems = false;
  @Input() includeMerged = false;
  @Input() moreActions: BulkAction[] = [];
  views: GridViewsState;
  refreshViews = new EventEmitter<GridView>(true);
  private sink = new SubSink();

  constructor(
    private chartService: ChartService,
    private chartLakeStatisticsService: ChartLakeStatisticsService,
    private memberService: MemberV2Service,
    private formService: FormService,
    private messagingService: MessagingService,
    private gridStateService: GridStateService,
    private gridViewsService: GridViewsService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sink.add(
      this.refreshViews.subscribe(gridView => this.getViews(gridView))
    );
    this.buildForm();
    this.createGrid();
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  private getViews(gridView: GridView | null = null): void {
    if (this.configuration.showViews) {
      this.gridViewsService.get(this.configuration.viewAttributeId).subscribe(views => {
        this.views = views;

        if (gridView != null) {
            setTimeout(() => this.basicGridComponent.onViewSelect.emit(gridView));
        }

        this.changeDetector.markForCheck();
      });
    }
  }

  fetchMemberCharts(event: string = "", includeMergedMemberCharts = false): void {
    const payload = {
      paginationToken: event,
      pageSize: this.configuration.pageSize,
      memberId: this.routeParameters.memberId,
    };
    this.memberService
      .getMemberCharts(payload, includeMergedMemberCharts, false)
      .subscribe(this.assignAndNotify, this.showFailedMessage);
  }

  fetchCharts(event: string = ""): void {
    const isSearch = ObjectHelper.isEmpty(this.filters) ? false : true;
    let payload = {
      paginationToken: event,
      pageSize: this.configuration.pageSize,
    };
    if (isSearch) {
      payload = { ...payload, ...this.filters };
    }
    this.chartService
      .getChartsList(payload, isSearch)
      .subscribe(this.assignAndNotify, this.showFailedMessage);
  }

  buildForm(): void {
    this.memberId = new Textbox({
      key: "memberId",
      label: "Customer Member ID",
      placeholder: "MEMBER ID",
    });

    this.memberFirstName = new Textbox({
      key: "memberFirstName",
      label: "Member First Name",
      placeholder: "FIRST NAME",
    });

    this.memberLastName = new Textbox({
      key: "memberLastName",
      label: "Member Last Name",
      placeholder: "LAST NAME",
    });

    this.memberDateOfBirth = new Textbox({
      key: "memberDateOfBirth",
      label: "Member Date of Birth",
      placeholder: "YYYY-MM-DD",
    });

    this.chartId = new Textbox({
      key: "chartId",
      label: "Chart ID",
      placeholder: "CHART ID",
    });

    this.providerName = new Textbox({
      key: "providerName",
      label: "Provider Name",
      placeholder: "PROVIDER NAME",
    });

    this.groupName = new Textbox({
      key: "groupName",
      label: "Group Name",
      placeholder: "GROUP NAME",
    });

    this.dateOfServiceFrom = new Textbox({
      key: "dateOfServiceFrom",
      label: "Date of Service From",
      placeholder: "YYYY-MM-DD",
    });

    this.dateOfServiceTo = new Textbox({
      key: "dateOfServiceTo",
      label: "Date of Service To",
      placeholder: "YYYY-MM-DD",
    });

    this.form = this.formService.createFormGroup([
      this.chartId,
      this.memberFirstName,
      this.memberLastName,
      this.memberId,
      this.providerName,
      this.groupName,
      this.dateOfServiceFrom,
      this.dateOfServiceTo,
      this.memberDateOfBirth,
    ]);

    this.form.valueChanges.subscribe(values => {
      this.validateDOS(values);
    });
    this.changeDetector.markForCheck();
  }

  validateDOS(values: any): void {
    const from = values.dateOfServiceFrom;
    const to = values.dateOfServiceTo;
    // date length: 10 - (yyyy-mm-dd)
    const DATE_LENGTH = 10;

    if (
      from &&
      from.length === DATE_LENGTH &&
      to &&
      to.length === DATE_LENGTH
    ) {
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const isFromValid = DateHelper.isAvailable(fromDate);
      const isToValid = DateHelper.isAvailable(toDate);
      if (!isFromValid || !isToValid) {
        this.messagingService.showToast(
          "Please enter valid dates in 'DOS From' and 'DOS To'",
          SeverityType.WARN
        );
        this.form.reset();
      } else if (toDate < fromDate) {
        this.messagingService.showToast(
          "'DOS To' date cannot be before 'DOS From' date",
          SeverityType.WARN
        );
        this.form.reset();
      }
    }
  }

  private assignAndNotify = (data: any): void => {
    if (!ObjectHelper.isEmpty(data)) {
      const tenantId = data.chartMetadatas[0]?.tenantId;
      if (tenantId) {
      this.chartLakeStatisticsService
          .getTenantStatistics(tenantId).subscribe((stats: TenantStatisticsResponse) => {
            this.totalChartCount = stats.chartCount;
            this.changeDetector.markForCheck();
          });
      }
    }
    if (data.paginationToken) {
      this.paginationComponent.pushToNext(data.paginationToken);
    }

    const rows: any[] = data.chartMetadatas.map((chart: any): any => {
      return {
        ...chart,
        memberFirstName: chart.firstName,
        memberLastName: chart.lastName,
        memberDateOfBirth: chart.dob,
        chartType: chart.chartType?.toLowerCase() === "traditional" ? "Image" : chart.chartType,
      } as any;
    });

    const makeCompositeKey = (r: any) =>
      `${r.memberId}::${StringHelper.clean(r.firstName).toLowerCase()}::${StringHelper.clean(r.lastName).toLowerCase()}::${StringHelper.clean(r.dob)}`;

    const compositeToObs = new Map<string, Observable<{ key: string; mem: string | null }>>();

    rows.forEach((r: any) => {
      const id = r.memberId;
      if (!StringHelper.isAvailable(id)) {
        return;
      }
      const key = makeCompositeKey(r);
      if (!compositeToObs.has(key)) {
        compositeToObs.set(
          key,
          this.getMemRecordId(r).pipe(
            catchError(() => of(null)),
            map(mem => ({ key, mem })),
            shareReplay(1)
          )
        );
      }
    });

    if (compositeToObs.size === 0) {
      this.chartData = rows;
      this.rows = this.configuration.pageSize;
      this.changeDetector.markForCheck();
      return;
    }

    // forkJoin all unique composite-key observables
    this.sink.add(
      forkJoin(Array.from(compositeToObs.values())).subscribe(
        results => {
          const memMap = new Map(results.map(x => [x.key, x.mem]));
          this.chartData = rows.map(r => ({
            ...r,
            memRecordId: memMap.get(makeCompositeKey(r)) || null,
          }));
          this.rows = this.configuration.pageSize;
          this.changeDetector.markForCheck();
        },
        err => {
          // fallback: use rows without memRecordId if forkJoin failed
          this.chartData = rows;
          this.rows = this.configuration.pageSize;
          this.changeDetector.markForCheck();
        }
      )
    );
  }

  showFailedMessage = (err: any): void => {
    const msg = err?.message || "Failed to load charts";
    this.messagingService.showToast(msg, SeverityType.ERROR);
  }

  createGrid(): void {
    this.configuration = new GridConfiguration({
      columns: this.getColumns(),
      pageSize: 25,
      pageSizeOptions: [25, 50, 100],
      hasPagination: false,
      stateName: this.stateName,
      showViews: true,
      viewAttributeId: this.viewAttributeId,
      showExportAction: false,
      showActionColumn: false,
      selectionMode: "multiple",
      showMenu: true,
      bulkActions: [
        new BulkAction({
          name: "Download Charts",
          action: this.downloadCharts,
          alwaysEnabled: true,
          showBulkAction: false,
        }),
        ...this.moreActions,
      ],
    });
    this.actions = this.configuration.bulkActions;
    this.getViews();
    this.configuration.filterGridFn = this.onApplyFilter;
    this.bindGridFromState();
    this.makeRequest();
  }

  downloadCharts = (selectedItems: any[]): void => {
    const rowChart = ArrayHelper.isAvailable(selectedItems) ? selectedItems : [selectedItems];
    rowChart.forEach((data: any) => {
      this.downloadSelectedChaseChart(data);
    });
  }

  private downloadSelectedChaseChart(rowData: any): void {
    if (rowData.totalPageCount > 0) {
      this.chartService.submitChartDownloadRequest(rowData.chartId)
        .subscribe((result: string) => {
          if (StringHelper.isAvailable(result)) {
            this.messagingService.showToast(CHART_DOWNLOAD_REQUEST_SUCCESS, SeverityType.SUCCESS);
          } else {
            this.messagingService.showToast(CHART_DOWNLOAD_REQUEST_FAILURE, SeverityType.ERROR);
          }
        });
    } else {
      this.messagingService.showToast("Chart not available for download.", SeverityType.ERROR);
    }
  }


  makeRequest(event: string = ""): void {
    if (this.hasRouteParamaters) {
      this.fetchMemberCharts(event, this.includeMerged);
    } else {
      this.fetchCharts(event);
    }
  }

  get hasRouteParamaters(): boolean {
    if (this.routeParameters == null) {
      return false;
    }
    const parametersWithValues = Object.values(this.routeParameters).filter(x => StringHelper.isAvailable(x));
    return ArrayHelper.isAvailable(parametersWithValues);
  }

  bindGridFromState(): void {
    const state = this.gridStateService.get(this.stateName);
    if (state?.basicGridState?.basicGridForm && !this.hasRouteParamaters) {
      const stateForm = JSON.parse(state.basicGridState.basicGridForm);
      this.request = state.basicGridState.basicGridRequest;
      this.filters = this.buildFilterFromRequest();
      this.bindFormValues(stateForm);
      this.formService.updateDom.next();
    }
  }

  bindFormValues(stateForm: any): void {
    for (const key in this.form.controls) {
      if (key) {
        switch (key) {
          case this.memberFirstNameInput.key:
            this.form.patchValue({ firstName: stateForm[key] });
            this.form.controls[
              this.memberFirstNameInput.key
            ].updateValueAndValidity({
              emitEvent: true,
            });
            this.memberFirstNameInput.value = stateForm[key];
            break;
          case this.memberLastNameInput.key:
            this.form.patchValue({ lastName: stateForm[key] });
            this.form.controls[
              this.memberLastNameInput.key
            ].updateValueAndValidity({
              emitEvent: true,
            });
            this.memberLastNameInput.value = stateForm[key];
            break;
          case this.memberIdInput.key:
            this.form.patchValue({ memberId: stateForm[key] });
            this.form.controls[this.memberIdInput.key].updateValueAndValidity({
              emitEvent: true,
            });
            this.memberIdInput.value = stateForm[key];
            break;
          case this.providerNameInput.key:
            this.form.patchValue({ providerName: stateForm[key] });
            this.form.controls[
              this.providerNameInput.key
            ].updateValueAndValidity({
              emitEvent: true,
            });
            this.providerNameInput.value = stateForm[key];
            break;
          case this.chartIdInput.key:
            this.form.patchValue({ chartId: stateForm[key] });
            this.form.controls[this.chartIdInput.key].updateValueAndValidity({
              emitEvent: true,
            });
            this.chartIdInput.value = stateForm[key];
            break;
          case this.dobInput.key:
            this.form.patchValue({ dob: stateForm[key] });
            this.form.controls[this.dobInput.key].updateValueAndValidity({
              emitEvent: true,
            });
            this.dobInput.value = stateForm[key];
            break;
          case this.dateOfServiceFromInput.key:
            this.form.patchValue({ dateOfServiceFrom: stateForm[key] });
            this.form.controls[
              this.dateOfServiceFromInput.key
            ].updateValueAndValidity({
              emitEvent: true,
            });
            this.dateOfServiceFromInput.value = stateForm[key];
            break;
          case this.dateOfServiceToInput.key:
            this.form.patchValue({ dateOfServiceTo: stateForm[key] });
            this.form.controls[
              this.dateOfServiceToInput.key
            ].updateValueAndValidity({
              emitEvent: true,
            });
            this.dateOfServiceToInput.value = stateForm[key];
            break;
          case this.groupNameInput.key:
            this.form.patchValue({ groupName: stateForm[key] });
            this.form.controls[this.groupNameInput.key].updateValueAndValidity({
              emitEvent: true,
            });
            this.groupNameInput.value = stateForm[key];
            break;
          default:
            this.form.get(key)?.setValue(stateForm[key]);
            break;
        }
      }
    }
    this.formService.updateDom.next();
  }

  private getColumns(): GridColumnDefinition[] {
    const defaultColumns = [
      new GridColumnDefinition({
        field: "chartId",
        header: "Chart ID",
        routeUrl: "/chartlake/charts/:chartId",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "memberId",
        header: "Customer Member ID",
        routeUrl: "/chartlake/membersnew/:memberId/:memRecordId",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "memberFirstName",
        header: "Member First Name",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "memberLastName",
        header: "Member Last Name",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "memberDateOfBirth",
        header: "Member DOB",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({ field: "fileName", header: "File Name", isSortableColumn: false }),
      new GridColumnDefinition({
        field: "totalPageCount",
        header: "Page Count",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "providerName",
        header: "Provider Name",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "groupName",
        header: "Group Name",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "dateOfServiceFrom",
        header: "DOS From",
        show: false,
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "dateOfServiceTo",
        header: "DOS To",
        show: false,
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "dateOfServiceRange",
        header: "Date(s) of Service",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "chartType",
        header: "Chart Type",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "sourceSystem",
        header: "Chart Source",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "trackingNumber",
        header: "Tracking Number",
        isSortableColumn: false,
      }),
      new GridColumnDefinition({
        field: "memRecordId",
        show: false,
        header: "Mem Record ID",
        isSortableColumn: false,
      }),
      ...this.additionalColumns,
    ];

    return defaultColumns;
  }

  onRemoveFilter(event: any): void {
    if (event.reset) {
      this.form.reset();
    } else if (
      this.form.get(event.text) &&
      ArrayHelper.isAvailable(this.form.get(event.text)?.value)
    ) {
      const filteredValue = this.form
        .get(event.text)
        ?.value.filter((item: any) => item.value !== event.value);
      this.form.get(event.text)?.setValue(filteredValue);
    } else {
      this.form.get(event.text)?.reset();
    }
    this.saveFilterState(event);
    this.makeRequest();
  }

  saveFilterState(event?: IFilter): void {
    const state = this.gridStateService.get(this.stateName);
    if (event) {
      this.request = state.basicGridState?.basicGridRequest;
      this.filters = this.buildFilterFromRequest();
    }
    if (state.basicGridState) {
      state.basicGridState.basicGridForm = JSON.stringify(this.form.value);
      state.basicGridState.basicGridRequest = event?.reset ? [] : this.request;
      this.gridStateService.put(this.stateName, state);
    }
    this.clearPagination();
  }

  clearPagination(): void {
    this.paginationComponent.clearHistory();
  }

  clearFilters(): void {
    this.request = [];
    this.filters = null;
  }

  buildFilterFromRequest(): any {
    return this.request?.reduce((obj: any, item: any) => {
      const key = item.text as string | undefined;
      if (key && StringHelper.isAvailable(key)) {
        obj[key] = item.value;
      }
      return obj;
    },                          {} as any);
  }

  checkForChartId() {
    const chartIdValue = this.form.get("chartId")?.value;

    if (chartIdValue) {
      this.messagingService.showToast(
          "ChartID filter added. Removing other filters",
          SeverityType.WARN
        );
      Object.keys(this.form.controls).forEach(key => {
      if (key !== "chartId") {
        this.form.get(key)?.reset();
      }
    });
  }
  }

  onApplyFilter = () => {
    this.checkForChartId();
    const newRequest: IFilter[] = [];
    Object.keys(this.form.value).forEach(key => {
      if (
        this.form.value[key] != null &&
        !Array.isArray(this.form.value[key]) &&
        StringHelper.isAvailable(this.form.value[key])
      ) {
        newRequest.push({
          text: key,
          value: this.form.value[key].value
            ? this.form.value[key].text
            : this.form.value[key],
          label: this.form.value[key].value ? key : (this as any)[key].label,
          controlType: (this as any)[key] ? (this as any)[key].controlType : "",
        });
      }
      if (ArrayHelper.isAvailable(this.form.value[key])) {
        this.form.value[key].map((item: any) =>
          newRequest.push({
            text: key,
            value: item.value,
            label: (this as any)[key].label,
            controlType: (this as any)[key].controlType,
          })
        );
      }
    });
    this.request = newRequest;
    this.filters = this.buildFilterFromRequest();
    this.saveFilterState();
    this.makeRequest();
  }

  getMemRecordId = (row: any): Observable<string | null> => {

    return this.memberService.getMemberById(row.memberId).pipe(
    map((states: any[]) => {
      if (!ArrayHelper.isAvailable(states)) {
        return null;
      }

      const targetFirst = StringHelper.clean(row.firstName).toLowerCase();
      const targetLast = StringHelper.clean(row.lastName).toLowerCase();
      const targetDob = StringHelper.clean(row.dob);

      for (const state of states) {
        if (!ArrayHelper.isAvailable(state.records)) { continue; }
        const match = state.records.find((rec: any) => {
          const recFirst = StringHelper.clean(rec.firstName).toLowerCase();
          const recLast = StringHelper.clean(rec.lastName).toLowerCase();
          const recDob = StringHelper.clean(rec.dob);
          return recFirst === targetFirst && recLast === targetLast && recDob === targetDob;
        });
        if (match) {
          return match.memRecordId;
        }
      }
      return null;
    })
  );
}


  validateFilterForm(): boolean {
    if (!this.form) {
      return false;
    }
    const firstName = this.form.get("memberFirstName")?.value;
    const lastName = this.form.get("memberLastName")?.value;
    const memberId = this.form.get("memberId")?.value;
    const providerName = this.form.get("providerName")?.value;
    const chartId = this.form.get("chartId")?.value;

    return firstName || lastName || memberId || providerName || chartId
      ? true
      : false;
  }

  get memberFirstNameInput(): Textbox {
    return this.memberFirstName;
  }

  get memberLastNameInput(): Textbox {
    return this.memberLastName;
  }

  get memberIdInput(): Textbox {
    return this.memberId;
  }

  get dobInput(): Textbox {
    return this.memberDateOfBirth;
  }

  get providerNameInput(): Textbox {
    return this.providerName;
  }

  get groupNameInput(): Textbox {
    return this.groupName;
  }

  get chartIdInput(): Textbox {
    return this.chartId;
  }

  get dateOfServiceFromInput(): Textbox {
    return this.dateOfServiceFrom;
  }

  get dateOfServiceToInput(): Textbox {
    return this.dateOfServiceTo;
  }
}
